import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import "./Register.css";
import { validateCityZip } from "../services/agentService";
import NavUser from "../components/NavUser";
import { FaArrowLeft, FaIdCard, FaFileAlt, FaCheckCircle, FaMapMarkerAlt, FaCloudUploadAlt, FaImage } from "react-icons/fa";

function TravelerToGuideRegister() {
  const navigate = useNavigate();
  const location = useLocation();
  const BACKEND_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

  const [currentStep, setCurrentStep] = useState(1);
  const [isSingleStepUpdate, setIsSingleStepUpdate] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Data States
  const [travelerData, setTravelerData] = useState(null);
  const [nationalId, setNationalId] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [dob, setDob] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [experience, setExperience] = useState("");
  const [languages, setLanguages] = useState("");
  const [preferredCurrency, setPreferredCurrency] = useState("");
  const [description, setDescription] = useState("");
  const [genderId, setGenderId] = useState("");
  const [nationalityId, setNationalityId] = useState("");
  const [countryId, setCountryId] = useState("");

  // Options Lists
  const [genders, setGenders] = useState([]);
  const [nationalities, setNationalities] = useState([]);
  const [countries, setCountries] = useState([]);

  // Files
  const [nicFront, setNicFront] = useState(null);
  const [nicBack, setNicBack] = useState(null);
  const [billFront, setBillFront] = useState(null);
  const [billBack, setBillBack] = useState(null);
  const [businessReg, setBusinessReg] = useState(null);
  const [businessLogo, setBusinessLogo] = useState(null);

  const [isRestrictedNonSriLankan, setIsRestrictedNonSriLankan] = useState(false);

  useEffect(() => {
    async function loadDetails() {
      try {
        const storedUser = JSON.parse(localStorage.getItem("user"));
        if (!storedUser || !storedUser.id) return;

        const baseUrl = BACKEND_URL.replace("/traveler-to-guide", "");
        const userRes = await fetch(`${baseUrl}/users/${storedUser.id}`);
        if (userRes.ok) {
          const uData = await userRes.json();
          const countryName = (uData.Country?.name || "").toLowerCase();
          if (uData.country_id && !countryName.includes("sri lanka") && countryName.length > 0) {
            setIsRestrictedNonSriLankan(true);
          }
        }

        // Load existing data for correction if applicable
        const res = await fetch(`${BACKEND_URL}/traveler-to-guide/my-details/${storedUser.id}`);
        if (res.ok) {
          const data = await res.json();
          setTravelerData(data.user);
          setNationalId(data.user.national_id_number || "");
          setPhoneNumber(data.user.phone_number || "");
          setDob(data.user.dob ? data.user.dob.split('T')[0] : "");
          setAddress(data.user.address || "");
          setGenderId(data.user.gender_id || "");
          setNationalityId(data.user.nationality_id || "");
          setCountryId(data.user.country_id || "");
          
          if (data.guide) {
            setCity(data.guide.city || "");
            setZipCode(data.guide.zip_code || "");
            if (data.guide.address) setAddress(data.guide.address);
            setSpecialty(data.guide.specialty || "");
            setExperience(data.guide.experience || "");
            setLanguages(data.guide.language || "");
            setPreferredCurrency(data.guide.preferred_currency || "");
            setDescription(data.guide.description || "");
          }
        }
      } catch (err) {
        console.error("Failed to load guide details:", err);
      }
    }
    async function loadMeta() {
      try {
        const baseUrl = BACKEND_URL.replace("/traveler-to-guide", "");
        const [gRes, nRes, cRes] = await Promise.all([
          fetch(`${baseUrl}/genders`),
          fetch(`${baseUrl}/nationalities`),
          fetch(`${baseUrl}/countries`)
        ]);
        if (gRes.ok) setGenders(await gRes.json());
        if (nRes.ok) {
          const allNats = await nRes.json();
          setNationalities(allNats.filter(n => n.name.toLowerCase().includes("sri lanka")));
        }
        if (cRes.ok) {
          const allCountries = await cRes.json();
          setCountries(allCountries.filter(c => c.name.toLowerCase().includes("sri lanka")));
        }
      } catch (err) {
        console.error("Meta load error:", err);
      }
    }
    loadDetails();
    loadMeta();
  }, [BACKEND_URL]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const targetStep = parseInt(params.get("step"));
    if (targetStep >= 1 && targetStep <= 3) {
      setCurrentStep(targetStep);
      setIsSingleStepUpdate(true);
    }
  }, [location.search]);

  const handleNext = () => setCurrentStep(prev => Math.min(prev + 1, 3));
  const handlePrev = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const handleFilePick = (e, setter) => {
    if (e.target.files && e.target.files[0]) setter(e.target.files[0]);
  };

  const uploadFile = async (file, folder) => {
    if (!file) return null;
    const fd = new FormData();
    fd.append("destination", folder);
    fd.append("file", file);
    const uploadUrl = BACKEND_URL.replace("/traveler-to-guide", "") + "/upload";
    const res = await fetch(uploadUrl, { method: "POST", body: fd });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Upload failed");
    return data.path;
  };

  const validateStep2Fields = () => {
    // Validate National ID / Passport
    const trimmedNic = (nationalId || "").trim().toUpperCase();
    const oldNic = /^\d{9}[VX]$/;
    const newNic = /^\d{12}$/;
    const passport = /^[A-Z0-9]{6,12}$/;
    if (!oldNic.test(trimmedNic) && !newNic.test(trimmedNic) && !passport.test(trimmedNic)) {
      Swal.fire({ title: "Validation Error", text: "Invaild National ID / Passport Number", icon: "warning" });
      setErrorMessage("Invaild National ID / Passport Number");
      return false;
    }

    // Validate Phone Number
    const phoneClean = (phoneNumber || "").trim().replace(/\s+/g, "");
    if (!/^(\+?[1-9]\d{8,14}|0\d{9})$/.test(phoneClean)) {
      Swal.fire({ title: "Validation Error", text: "Invaild Phone Number", icon: "warning" });
      setErrorMessage("Invaild Phone Number");
      return false;
    }

    // Validate DOB / Age >= 20
    if (!dob) {
      Swal.fire({ title: "Validation Error", text: "Please enter your birth date.", icon: "warning" });
      setErrorMessage("Please enter your birth date.");
      return false;
    }
    const birthday = new Date(dob);
    if (isNaN(birthday.getTime())) {
      Swal.fire({ title: "Validation Error", text: "Please enter a valid date format.", icon: "warning" });
      setErrorMessage("Please enter a valid date format.");
      return false;
    }
    const today = new Date();
    let calculatedAge = today.getFullYear() - birthday.getFullYear();
    if (
      today.getMonth() < birthday.getMonth() ||
      (today.getMonth() === birthday.getMonth() && today.getDate() < birthday.getDate())
    ) {
      calculatedAge--;
    }
    if (calculatedAge < 20) {
      Swal.fire({ title: "Validation Error", text: "You must be at least 20 years old", icon: "warning" });
      setErrorMessage("You must be at least 20 years old");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    // Step 1 Validation
    if (currentStep === 1) {
      const isValid = await validateCityZip(city, zipCode);
      if (!isValid) {
        Swal.fire({ title: "Validation Error", text: "City and Zip code do not match.", icon: "warning" });
        setErrorMessage("City and Zip code do not match.");
        return;
      }
      if (!isSingleStepUpdate) {
        handleNext();
        return;
      }
    }

    // Step 2 Validation
    if (currentStep === 2) {
      if (!validateStep2Fields()) {
        return;
      }
      if (!isSingleStepUpdate) {
        handleNext();
        return;
      }
    }

    if (!travelerData) {
      setErrorMessage("Please wait for profile data to load.");
      return;
    }

    try {
      setIsSubmitting(true);

      // Section 1: Guide Information
      if (!isSingleStepUpdate || currentStep === 1) {
        const isValid = await validateCityZip(city, zipCode);
        if (!isValid) {
          setErrorMessage("City and Zip code do not match.");
          setIsSubmitting(false);
          return;
        }

        const uploadedLogo = businessLogo ? await uploadFile(businessLogo, "guideLogo") : null;

        const profilePayload = {
          user_id: travelerData.id,
          city,
          zip_code: zipCode,
          address,
          specialty,
          experience,
          language: languages,
          preferred_currency: preferredCurrency,
          description,
          business_logo: uploadedLogo
        };

        const res = await fetch(`${BACKEND_URL}/traveler-to-guide/profile`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(profilePayload),
        });

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.message || "Failed to save profile information.");
        }
      }

      // Section 2: Personal Details
      if (!isSingleStepUpdate || currentStep === 2) {
        if (!validateStep2Fields()) {
          setIsSubmitting(false);
          return;
        }

        const personalPayload = {
          user_id: travelerData.id,
          national_id_number: nationalId,
          phone_number: phoneNumber,
          dob: dob,
          gender_id: genderId,
          nationality_id: nationalityId,
          country_id: countryId
        };

        const res = await fetch(`${BACKEND_URL}/traveler-to-guide/personal-details`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(personalPayload),
        });

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.message || "Failed to save personal details.");
        }
      }

      // Section 3: Verification Documents
      if (!isSingleStepUpdate || currentStep === 3) {
        const uploadedNicFront = nicFront ? await uploadFile(nicFront, "nicDoc") : null;
        const uploadedNicBack = nicBack ? await uploadFile(nicBack, "nicDoc") : null;
        const uploadedBillFront = billFront ? await uploadFile(billFront, "guideVerification") : null;
        const uploadedBillBack = billBack ? await uploadFile(billBack, "guideVerification") : null;
        const uploadedReg = businessReg ? await uploadFile(businessReg, "guideBusinessRegistratioinQualification") : null;

        if (!isSingleStepUpdate && (!uploadedNicFront || !uploadedNicBack || !uploadedBillFront || !uploadedBillBack)) {
          throw new Error("Please upload all required verification documents.");
        }

        const docsPayload = {
          user_id: travelerData.id,
          nic_or_driving_licence_front: uploadedNicFront,
          nic_or_driving_licence_back: uploadedNicBack,
          water_or_light_bill_front: uploadedBillFront,
          water_or_light_bill_back: uploadedBillBack,
          business_registration_or_qualifications: uploadedReg
        };

        const res = await fetch(`${BACKEND_URL}/traveler-to-guide/verification-documents`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(docsPayload),
        });

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.message || "Failed to save verification documents.");
        }
      }

      // Update local storage user state to Guide (2) with status Pending (3)
      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      const updatedUserObj = { ...storedUser, ...travelerData, role_id: 2, status_id: 3 };
      localStorage.setItem("user", JSON.stringify(updatedUserObj));

      if (isSingleStepUpdate) {
        await fetch(`${BACKEND_URL}/traveler-to-guide/notify-update`, {
          method: "POST",
          headers: { "Authorization": `Bearer ${localStorage.getItem("jwt_token")}` }
        });

        Swal.fire({
          title: "Update Success!",
          text: "Your information has been updated and admin has been notified.",
          icon: "success"
        }).then(() => navigate(`/travelerhome/${travelerData.id}`));
      } else {
        Swal.fire({
          title: "Registration Success!",
          text: "Your guide profile is pending admin approval.",
          icon: "success"
        }).then(() => navigate(`/travelerhome/${travelerData.id}`));
      }

    } catch (err) {
      Swal.fire({ title: "Error", text: err.message, icon: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="agent-page-premium">
      <NavUser />
      
      <div className="premium-hero-mini">
        <div className="hero-content">
          <h1>Guide the Way</h1>
          <p>Share your local expertise and lead unforgettable journeys.</p>
        </div>
      </div>

      <div className="premium-container-wide">
        {isRestrictedNonSriLankan ? (
          <div className="premium-glass-card restriction-card" style={{ padding: "40px", textAlign: "center", maxWidth: "680px", margin: "40px auto", borderRadius: "16px", background: "#ffffff", boxShadow: "0 10px 30px rgba(0,0,0,0.08)", border: "1px solid #e2e8f0" }}>
            <div style={{ width: "80px", height: "80px", background: "#fef2f2", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
              <FaIdCard size={36} color="#ef4444" />
            </div>
            <h2 style={{ fontSize: "24px", fontWeight: "800", color: "#1e293b", marginBottom: "12px" }}>
              Restricted to Sri Lankan Licensed Tour Guides
            </h2>
            <p style={{ fontSize: "15px", color: "#64748b", lineHeight: "1.6", marginBottom: "24px" }}>
              Under Sri Lanka Tourism Development Authority (SLTDA) regulations, professional tour guide registrations are reserved exclusively for Sri Lankan national and site guides with valid national identification.
            </p>
            <div style={{ background: "#f8fafc", borderRadius: "12px", padding: "16px 20px", marginBottom: "30px", textAlign: "left", border: "1px solid #e2e8f0" }}>
              <p style={{ fontSize: "14px", color: "#334155", margin: 0, fontWeight: "600" }}>
                🌍 <strong>As an International Traveler, you can:</strong>
              </p>
              <ul style={{ fontSize: "13.5px", color: "#64748b", marginTop: "8px", paddingLeft: "20px", lineHeight: "1.6" }}>
                <li>Discover curated heritage, wildlife, and adventure tours across Sri Lanka</li>
                <li>Hire verified, multilingual Sri Lankan tour guides for customized itineraries</li>
                <li>Apply for your Sri Lanka Electronic Travel Authorization (ETA)</li>
              </ul>
            </div>
            <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
              <button className="btn-premium-action" onClick={() => navigate(-1)} style={{ padding: "12px 24px" }}>
                ← Return to Dashboard
              </button>
            </div>
          </div>
        ) : (
        <div className="premium-glass-card">
          <div className="card-header-premium">
            <button onClick={() => navigate(-1)} className="btn-back-minimal">
              <FaArrowLeft />
            </button>
            <div className="header-titles">
              <h2>Guide Registration</h2>
              <p>Step {currentStep} of 3: {currentStep === 1 ? "Guide Information" : currentStep === 2 ? "Personal Details" : "Verification Documents"}</p>
            </div>
          </div>

          {!isSingleStepUpdate && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '30px', gap: '10px' }}>
               {[1, 2, 3].map(s => (
                 <div key={s} style={{ width: '30%', height: '4px', background: s <= currentStep ? '#f97316' : '#e5e7eb', borderRadius: '2px', transition: 'background 0.3s' }}></div>
               ))}
            </div>
          )}

          <form className="premium-form-layout" onSubmit={handleSubmit}>
            <div className="form-grid-2">
              {currentStep === 1 && (
                <div className="form-column">
                  <div className="section-header-minimal">
                    <FaMapMarkerAlt /> <span>Location & Brand</span>
                  </div>
                  
                  <div className="form-grid-2" style={{ gap: '20px' }}>
                    <div className="premium-field">
                      <label>Specialty</label>
                      <input placeholder="e.g. Cultural, Adventure" value={specialty} onChange={(e) => setSpecialty(e.target.value)} required />
                    </div>
                    <div className="premium-field">
                      <label>Languages</label>
                      <input placeholder="e.g. English, French" value={languages} onChange={(e) => setLanguages(e.target.value)} required />
                    </div>
                  </div>

                  <div className="form-grid-2" style={{ gap: '20px' }}>
                    <div className="premium-field">
                      <label>Experience</label>
                      <input placeholder="e.g. 5 Years" value={experience} onChange={(e) => setExperience(e.target.value)} required />
                    </div>
                    <div className="premium-field">
                      <label>Preferred Currency</label>
                      <input placeholder="e.g. USD, EUR" value={preferredCurrency} onChange={(e) => setPreferredCurrency(e.target.value)} required />
                    </div>
                  </div>

                  <div className="premium-field">
                    <label>Description / About You</label>
                    <textarea
                      placeholder="Tell travelers about your guiding style..."
                      rows="3"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      required
                    />
                  </div>

                  <div className="form-grid-2" style={{ gap: '20px' }}>
                    <div className="premium-field">
                      <label>City</label>
                      <input placeholder="Enter city" value={city} onChange={(e) => setCity(e.target.value)} required />
                    </div>
                    <div className="premium-field">
                      <label>Zip Code</label>
                      <input placeholder="Enter zip code" value={zipCode} onChange={(e) => setZipCode(e.target.value)} required />
                    </div>
                  </div>

                  <div className="premium-field">
                    <label>Permanent Address</label>
                    <textarea
                      placeholder="Enter your full legal address"
                      rows="2"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      required
                    />
                  </div>

                  <div className="premium-upload-item" style={{ marginTop: '20px' }}>
                    <label>Business Logo / Profile Picture</label>
                    <label className={`upload-zone-wide ${businessLogo ? 'active' : ''}`}>
                      <input type="file" accept="image/*" onChange={(e) => handleFilePick(e, setBusinessLogo)} />
                      <FaImage />
                      <span>{businessLogo ? businessLogo.name : "Upload Logo (PNG/JPG)"}</span>
                    </label>
                  </div>
                </div>
              )}

              {currentStep === 2 && (
                <div className="form-column">
                  <div className="section-header-minimal">
                    <FaIdCard /> <span>Identity Information</span>
                  </div>
                  
                  <div className="premium-field">
                    <label>National ID / Passport Number</label>
                    <input
                      placeholder="Enter ID number"
                      value={nationalId}
                      onChange={(e) => setNationalId(e.target.value)}
                      required
                    />
                  </div>

                  <div className="form-grid-2" style={{ gap: '20px' }}>
                    <div className="premium-field">
                      <label>Phone Number</label>
                      <input placeholder="Enter phone" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} required />
                    </div>
                    <div className="premium-field">
                      <label>Birth Date</label>
                      <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} required />
                    </div>
                  </div>

                  <div className="form-grid-3" style={{ gap: '15px', marginTop: '10px' }}>
                    <div className="premium-field">
                      <label>Gender</label>
                      <select value={genderId} onChange={(e) => setGenderId(e.target.value)} required style={{width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0'}}>
                        <option value="">Select</option>
                        {genders.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                      </select>
                    </div>
                    <div className="premium-field">
                      <label>Nationality</label>
                      <select value={nationalityId} onChange={(e) => setNationalityId(e.target.value)} required style={{width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0'}}>
                        <option value="">Select</option>
                        {nationalities.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
                      </select>
                    </div>
                    <div className="premium-field">
                      <label>Country</label>
                      <select value={countryId} onChange={(e) => setCountryId(e.target.value)} required style={{width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0'}}>
                        <option value="">Select</option>
                        {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 3 && (
                <div className="form-column">
                  <div className="section-header-minimal">
                    <FaFileAlt /> <span>Verification Documents</span>
                  </div>
                  <p style={{fontSize: '13px', color: '#666', marginBottom: '15px'}}>Only re-upload flagged files if correcting an issue.</p>

                  <div className="upload-grid-premium">
                    <div className="premium-upload-item">
                      <label>ID Front</label>
                      <label className={`upload-zone ${nicFront ? 'active' : ''}`}>
                        <input type="file" accept="image/*,pdf" onChange={(e) => handleFilePick(e, setNicFront)} />
                        <FaCloudUploadAlt />
                        <span>{nicFront ? nicFront.name : "Choose File"}</span>
                      </label>
                    </div>
                    <div className="premium-upload-item">
                      <label>ID Back</label>
                      <label className={`upload-zone ${nicBack ? 'active' : ''}`}>
                        <input type="file" accept="image/*,pdf" onChange={(e) => handleFilePick(e, setNicBack)} />
                        <FaCloudUploadAlt />
                        <span>{nicBack ? nicBack.name : "Choose File"}</span>
                      </label>
                    </div>
                    <div className="premium-upload-item">
                      <label>Utility Bill (Front)</label>
                      <label className={`upload-zone ${billFront ? 'active' : ''}`}>
                        <input type="file" accept="image/*,pdf" onChange={(e) => handleFilePick(e, setBillFront)} />
                        <FaCloudUploadAlt />
                        <span>{billFront ? billFront.name : "Choose File"}</span>
                      </label>
                    </div>
                    <div className="premium-upload-item">
                      <label>Utility Bill (Back)</label>
                      <label className={`upload-zone ${billBack ? 'active' : ''}`}>
                        <input type="file" accept="image/*,pdf" onChange={(e) => handleFilePick(e, setBillBack)} />
                        <FaCloudUploadAlt />
                        <span>{billBack ? billBack.name : "Choose File"}</span>
                      </label>
                    </div>
                  </div>

                  <div className="premium-field full-width" style={{ marginTop: '20px' }}>
                    <label>Registration / Qualifications (Optional)</label>
                    <label className={`upload-zone-wide ${businessReg ? 'active' : ''}`}>
                      <input type="file" accept="image/*,pdf" onChange={(e) => handleFilePick(e, setBusinessReg)} />
                      <FaCloudUploadAlt />
                      <span>{businessReg ? businessReg.name : "Upload supporting documents"}</span>
                    </label>
                  </div>
                </div>
              )}
            </div>

            {errorMessage && (
              <div className="form-error-premium" style={{ marginTop: '20px', padding: '12px', background: '#fee2e2', color: '#b91c1c', borderRadius: '10px', fontSize: '14px', textAlign: 'center' }}>
                {errorMessage}
              </div>
            )}

            <div className="action-row-centered" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '30px', width: '100%' }}>
              {!isSingleStepUpdate && currentStep > 1 ? (
                <button type="button" onClick={handlePrev} className="btn-premium-action" style={{ background: '#eee', color: '#333' }}>
                  Back
                </button>
              ) : <div style={{width: '100px'}}></div>}
              
              <button type="submit" className="btn-premium-action" disabled={isSubmitting}>
                {isSubmitting ? (
                  <span className="loader-dots">Processing...</span>
                ) : isSingleStepUpdate ? (
                  <>
                    <FaCheckCircle /> Update Details
                  </>
                ) : currentStep < 3 ? (
                  "Next Step"
                ) : (
                  <>
                    <FaCheckCircle /> Submit Registration
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
        )}
      </div>
    </div>
  );
}

export default TravelerToGuideRegister;
